import React, { useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  Download,
  ArrowUpRight,
  ArrowDownLeft,
  PieChart,
  BarChart3,
  Wallet,
  Users,
  Receipt,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// Mock financial data
const monthlyData = [
  {
    month: "Jan",
    distributed: 45000,
    received: 100000,
    campaigns: 3,
    users: 1200,
  },
  {
    month: "Feb",
    distributed: 52000,
    received: 75000,
    campaigns: 4,
    users: 1850,
  },
  {
    month: "Mar",
    distributed: 67000,
    received: 120000,
    campaigns: 5,
    users: 2400,
  },
  {
    month: "Apr",
    distributed: 58000,
    received: 90000,
    campaigns: 3,
    users: 2100,
  },
  {
    month: "May",
    distributed: 73000,
    received: 110000,
    campaigns: 6,
    users: 2800,
  },
  {
    month: "Jun",
    distributed: 89000,
    received: 150000,
    campaigns: 7,
    users: 3200,
  },
];

const tokenDistribution = [
  { name: "PXT", value: 450000, color: "#3b82f6" },
  { name: "GWT", value: 280000, color: "#10b981" },
  { name: "BTA", value: 120000, color: "#f59e0b" },
  { name: "MGT", value: 90000, color: "#ef4444" },
  { name: "PTN", value: 60000, color: "#8b5cf6" },
];

const campaignPerformance = [
  {
    name: "Project X Launch",
    distributed: 225000,
    participants: 3245,
    efficiency: 95,
  },
  {
    name: "Community Growth",
    distributed: 180000,
    participants: 1832,
    efficiency: 88,
  },
  {
    name: "Token Migration",
    distributed: 315000,
    participants: 5621,
    efficiency: 92,
  },
  {
    name: "Beta Testing",
    distributed: 78000,
    participants: 892,
    efficiency: 87,
  },
  {
    name: "Partnership",
    distributed: 145000,
    participants: 2156,
    efficiency: 90,
  },
];

const gasUsageData = [
  { month: "Jan", gasUsed: 2.5, gasCost: 850 },
  { month: "Feb", gasUsed: 3.2, gasCost: 1120 },
  { month: "Mar", gasUsed: 4.1, gasCost: 1435 },
  { month: "Apr", gasUsed: 3.8, gasCost: 1330 },
  { month: "May", gasUsed: 4.7, gasCost: 1645 },
  { month: "Jun", gasUsed: 5.3, gasCost: 1855 },
];

const FinancialOverview: React.FC = () => {
  const [dateRange, setDateRange] = useState("6m");

  const getTotalDistributed = () => {
    return monthlyData.reduce((sum, month) => sum + month.distributed, 0);
  };

  const getTotalReceived = () => {
    return monthlyData.reduce((sum, month) => sum + month.received, 0);
  };

  const getCurrentBalance = () => {
    return getTotalReceived() - getTotalDistributed();
  };

  const getDistributionEfficiency = () => {
    const total = getTotalDistributed();
    const target = getTotalReceived() * 0.8; // Assuming 80% target distribution
    return Math.round((total / target) * 100);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Financial Overview
          </h1>
          <p className="text-gray-600 mt-1">
            Comprehensive financial analytics and fund tracking
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1m">1 Month</SelectItem>
              <SelectItem value="3m">3 Months</SelectItem>
              <SelectItem value="6m">6 Months</SelectItem>
              <SelectItem value="1y">1 Year</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Distributed
                </p>
                <p className="text-2xl font-bold text-blue-600">
                  {getTotalDistributed().toLocaleString()}
                </p>
                <div className="flex items-center mt-2">
                  <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">+12.5%</span>
                </div>
              </div>
              <ArrowUpRight className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Received
                </p>
                <p className="text-2xl font-bold text-green-600">
                  {getTotalReceived().toLocaleString()}
                </p>
                <div className="flex items-center mt-2">
                  <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">+8.7%</span>
                </div>
              </div>
              <ArrowDownLeft className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Current Balance
                </p>
                <p className="text-2xl font-bold text-purple-600">
                  {getCurrentBalance().toLocaleString()}
                </p>
                <div className="flex items-center mt-2">
                  <Wallet className="h-4 w-4 text-gray-500 mr-1" />
                  <span className="text-sm text-gray-600">Available</span>
                </div>
              </div>
              <DollarSign className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Distribution Efficiency
                </p>
                <p className="text-2xl font-bold text-orange-600">
                  {getDistributionEfficiency()}%
                </p>
                <div className="flex items-center mt-2">
                  <BarChart3 className="h-4 w-4 text-gray-500 mr-1" />
                  <span className="text-sm text-gray-600">vs Target</span>
                </div>
              </div>
              <TrendingUp className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts and Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Token Flow Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Token Flow Trend</CardTitle>
            <CardDescription>
              Monthly distribution vs received tokens
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number) => [value.toLocaleString(), ""]}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="received"
                    stackId="1"
                    stroke="#10b981"
                    fill="#10b981"
                    fillOpacity={0.6}
                    name="Received"
                  />
                  <Area
                    type="monotone"
                    dataKey="distributed"
                    stackId="2"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.6}
                    name="Distributed"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Token Distribution Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Token Distribution by Symbol</CardTitle>
            <CardDescription>
              Total tokens distributed across different campaigns
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={tokenDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }: any) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {tokenDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number) => [
                      value.toLocaleString(),
                      "Tokens",
                    ]}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analytics Tabs */}
      <Tabs defaultValue="campaigns" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="campaigns">Campaign Performance</TabsTrigger>
          <TabsTrigger value="gas">Gas Analytics</TabsTrigger>
          <TabsTrigger value="users">User Growth</TabsTrigger>
          <TabsTrigger value="transactions">Transaction Volume</TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Campaign Financial Performance</CardTitle>
              <CardDescription>
                Distribution amounts and efficiency metrics by campaign
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {campaignPerformance.map((campaign, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">
                        {campaign.name}
                      </h3>
                      <div className="grid grid-cols-3 gap-4 mt-2">
                        <div>
                          <p className="text-sm text-gray-500">Distributed</p>
                          <p className="font-medium">
                            {campaign.distributed.toLocaleString()} tokens
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Participants</p>
                          <p className="font-medium">
                            {campaign.participants.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Efficiency</p>
                          <Badge
                            className={
                              campaign.efficiency >= 90
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }
                          >
                            {campaign.efficiency}%
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="gas" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Gas Usage Analytics</CardTitle>
              <CardDescription>
                Track gas consumption and costs for token distributions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={gasUsageData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        name === "gasUsed" ? `${value} ETH` : `$${value}`,
                        name === "gasUsed" ? "Gas Used" : "Gas Cost",
                      ]}
                    />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="gasUsed"
                      fill="#3b82f6"
                      name="Gas Used (ETH)"
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="gasCost"
                      fill="#ef4444"
                      name="Gas Cost ($)"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Gas Optimization Tips */}
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <BarChart3 className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-2">
                    Gas Optimization Tips
                  </h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>
                      • Batch multiple token distributions in single
                      transactions
                    </li>
                    <li>
                      • Monitor gas prices and schedule distributions during
                      low-cost periods
                    </li>
                    <li>
                      • Consider Layer 2 solutions for high-frequency
                      distributions
                    </li>
                    <li>
                      • Use gas estimation tools before large distributions
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>User Growth vs Token Distribution</CardTitle>
              <CardDescription>
                Correlation between user acquisition and token distribution
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="users"
                      stroke="#10b981"
                      strokeWidth={3}
                      name="New Users"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="distributed"
                      stroke="#3b82f6"
                      strokeWidth={3}
                      name="Tokens Distributed"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Monthly Transaction Volume</CardTitle>
              <CardDescription>
                Number of campaigns and transaction volume over time
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="campaigns"
                      fill="#f59e0b"
                      name="Active Campaigns"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Transaction Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Avg per Campaign</p>
                    <p className="text-xl font-semibold">2,847</p>
                  </div>
                  <Receipt className="h-6 w-6 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Success Rate</p>
                    <p className="text-xl font-semibold text-green-600">
                      98.5%
                    </p>
                  </div>
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Failed Txns</p>
                    <p className="text-xl font-semibold text-red-600">1.5%</p>
                  </div>
                  <TrendingDown className="h-6 w-6 text-red-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Financial Health Alert */}
      <Card className="border-green-200 bg-green-50">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <TrendingUp className="h-5 w-5 text-green-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-green-900 mb-1">
                Financial Health Status: Excellent
              </h4>
              <p className="text-sm text-green-800">
                Your platform maintains healthy token reserves with efficient
                distribution rates. Current balance can support approximately
                15-20 more campaigns of similar scale.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FinancialOverview;
